// Release 2 (F3): the router's page-access check, decided BEFORE the page opens.
//
// The old guard called next() unconditionally at the end, did not wait for the Role Permissions
// check (so the page and its data calls ran first and the redirect came afterwards), treated a
// browser with only one of userInfo / accessToken as logged in, and threw when userInfo was
// missing. Here next() is called exactly once on every path.
//
// The check itself is unchanged: a route whose meta has `action` needs that action on its
// `resource` in the user's role (Role Permissions page), loaded by defineAbilitiesFor().

const NO_ABILITY_CHECK = ["forgot-password", "page-error-404", "reset-password", "unauthorized"];

function readUserInfo(storage) {
  try {
    return JSON.parse(storage.getItem("userInfo"));
  } catch (e) {
    return null;
  }
}

export default function createRouteGuard({ defineAbilitiesFor, storage, reload }) {
  return function routeGuard(to, from, next) {
    const userInfo = readUserInfo(storage);
    const accessToken = storage.getItem("accessToken");
    const loggedIn = !!userInfo && !!accessToken;

    if (!loggedIn) {
      return to.meta && to.meta.authRequired ? next("/login") : next();
    }

    if (userInfo.status == "Suspended") {
      storage.removeItem("accessToken");
      storage.removeItem("userInfo");
      next("/login");
      reload();
      return;
    }

    const needsAbility = to.matched.some(route => route.meta && route.meta.action !== undefined);
    if (!needsAbility || NO_ABILITY_CHECK.includes(to.name)) {
      return next();
    }

    let pending;
    try {
      pending = Promise.resolve(defineAbilitiesFor());
    } catch (e) {
      pending = Promise.resolve(undefined);
    }
    return pending
      .catch(() => undefined)
      .then(abilities => {
        if (!abilities) {
          // The permissions could not be loaded (a 401 is already sent to the login page by
          // defineAbilitiesFor). Do not open the page: stay where the user is, or on a first
          // load, show the unauthorized page.
          return next(from && from.matched && from.matched.length ? false : "/unauthorized");
        }
        const allowed = to.matched.every(
          route => !route.meta || route.meta.action === undefined || abilities.can(route.meta.action, route.meta.resource)
        );
        return allowed ? next() : next("/unauthorized");
      });
  };
}
